import React from 'react';
import Serializer, {
  type SerializeArguments,
  type DeserializeArguments,
} from './Serializer';
import { BlockElements } from './SerializerBlock';
import findParent from '../../util/findParent';
import { normalizeHref } from '../../plugins/LinkPlugin/utils';
import { type LinkElement } from '../../types/aha-editor';
import { type Descendant } from 'slate';
import { isHTMLElementNode } from '../helpers/serializerHelpers';
import { isLinkElement } from '../../types/guards';

class SerializerLink extends Serializer {
  static deserialize({
    el,
    next,
    markAttributes,
  }: DeserializeArguments): LinkElement | Descendant[] | undefined {
    if (
      !isHTMLElementNode(el) ||
      el.tagName.toLowerCase() !== 'a' ||
      el.getAttribute('download') // Downloads are handled by the attachment serializer.
    )
      return;

    const href = el.getAttribute('href');
    const name = el.getAttribute('name');

    // If the link is an old style html anchor then add that to the parent block and continue.
    if (name && (!href || href.trim() === '')) {
      const parentBlock = findParent((parent) =>
        Object.keys(BlockElements).includes(parent.nodeName.toLowerCase())
      )(el);

      if (parentBlock) {
        parentBlock.setAttribute('data-anchor', name);
      }

      return next(el.childNodes, markAttributes);
    }

    return {
      type: 'link',
      link_href: href ?? '',
      children: next(el.childNodes, markAttributes),
    };
  }

  static serialize({ node, children }: SerializeArguments): React.ReactNode {
    if (!isLinkElement(node)) return;

    return <a href={normalizeHref(node.link_href)}>{children}</a>;
  }
}

export default SerializerLink;
